import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { Z } from './zLayers';

// T131 — shared slide-over primitive (left/right). Backdrop scrim + side panel
// + built-in focus trap + gated Escape/backdrop close. Inline-rendered (no
// portal). Width + scrim are configurable so it reproduces each existing drawer
// exactly (ToastHistory's w-96 + blurred scrim, SideDrawer's w-full max-w-md).
//
// tone: 'overlay' (z-55 — toast history, mobile nav) · 'drawer' (z-40 — the
// Applications SideDrawer, so the modals it opens render above it).
//
// Portalled to <body>, which the original inline version was not. `position:
// sticky` creates a stacking context, so the moment the sidebar was pinned
// (its rail holds the Activity and toast-history buttons) every drawer opened
// from inside it had its z-55 scoped to the rail instead of the page, and
// painted UNDER the content it is supposed to cover. A fixed-position overlay
// belongs at the top of the document; nesting it was always a latent bug and
// pinning the rail is what collected on it.

export default function Drawer({
  open = true,
  onClose,
  side = 'right',
  tone = 'overlay',
  mdHidden = false,
  panelClassName = 'w-full max-w-md',
  scrimClassName = 'bg-black/60',
  escapeClosesOnTop = true,
  // T248 — parity with Modal. JobDrawer focuses its close button on open rather
  // than the first focusable, and folding it in here must not silently drop
  // that; without the option the migration would have been a regression
  // disguised as a cleanup.
  initialFocusRef,
  labelledById,
  ariaLabel,
  role = 'dialog',
  children,
}) {
  const panelRef = useRef(null);
  useFocusTrap(panelRef, open, initialFocusRef);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && escapeClosesOnTop) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, escapeClosesOnTop]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const z = tone === 'drawer' ? Z.drawer : Z.overlay;
  const justify = side === 'right' ? 'justify-end' : 'justify-start';
  const border = side === 'right' ? 'border-l' : 'border-r';

  return createPortal(
    <div
      role={role}
      aria-modal="true"
      aria-labelledby={labelledById}
      aria-label={ariaLabel}
      className={`fixed inset-0 ${z} flex ${justify} ${scrimClassName} ${mdHidden ? 'md:hidden' : ''}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <aside
        ref={panelRef}
        tabIndex={-1}
        className={`flex h-full flex-col overflow-hidden ${border} border-slate-800 bg-slate-900 shadow-2xl outline-none ${panelClassName}`}
      >
        {children}
      </aside>
    </div>,
    document.body,
  );
}
